import React from 'react';
import PropTypes from 'prop-types';
import i18n from '../../../../i18n';
import { Box, ButtonBase, Paper, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import Colors from '../../../../library/Colors';
import { useAnchor } from '../../search/WebshopConfigSearch';

// Shared keyboard focus ring for custom ButtonBase controls (chips, tiles,
// swatches). MUI only sets `.Mui-focusVisible` on keyboard focus, so this stays
// invisible for mouse users but gives a clear ring when tabbing through.
export const focusRing = {
    '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
};

// ────────────────────────────────────────────────────────────────────────
// Shared building blocks for the Product Detail Information modal cards.
// Two patterns recur across multiple cards in the schema:
//   1. ChipToggle  — a draggable pill with a label + small toggle
//   2. AvailableChip — pill button "+ Label" that adds back into the list
//   3. CheckboxTile — filled-style checkbox tile (blue when checked)
// ────────────────────────────────────────────────────────────────────────

// ChipsBox: container for an ordered list of ChipToggle pills. Light grey
// rounded box that holds them in a wrap.
export const ChipsBox = ({ children, sx = {} }) => (
    <Box sx={{
        backgroundColor: 'rgba(0,0,0,0.03)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 1.25,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.75,
        minHeight: 48,
        ...sx,
    }}>
        {children}
    </Box>
);
ChipsBox.propTypes = { children: PropTypes.node, sx: PropTypes.object };

// One pill in a ChipsBox: label + × remove. The chip's mere presence in the
// list means the field is in use — visibility is binary (in the list = on,
// removed = off). NB: no drag affordance — these lists aren't reorderable
// (the old grab-cursor + handle icon promised a drag that didn't exist).
export const ChipToggle = ({ label, onRemove }) => (
    <Box sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        backgroundColor: '#fff',
        border: '1px solid',
        borderColor: 'primary.light',
        borderRadius: 999,
        pl: 0.75,
        pr: 0.75,
        py: 0.25,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    }}>
        <Typography variant="caption" sx={{
            fontWeight: 600,
            color: 'primary.main',
            px: 0.5,
            userSelect: 'none',
        }}>{label}</Typography>
        {onRemove && (
            <ButtonBase
                onClick={onRemove}
                sx={{
                    width: 18, height: 18, ml: 0.25,
                    borderRadius: '50%',
                    color: 'text.disabled',
                    fontSize: '1rem',
                    lineHeight: 1,
                    '&:hover': { color: 'error.main', backgroundColor: 'rgba(211,47,47,0.08)' },
                    ...focusRing,
                }}
                title={i18n.t('cfg_tt_remove')}
            >×</ButtonBase>
        )}
    </Box>
);
ChipToggle.propTypes = { label: PropTypes.node, onRemove: PropTypes.func };

// Pill-shaped "+ Label" button. Lives in the AVAILABLE row beneath an
// ordered list — clicking it adds the field back to the ordered list.
export const AvailableChip = ({ label, onAdd }) => (
    <ButtonBase
        onClick={onAdd}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 999,
            backgroundColor: '#fff',
            px: 1.25,
            py: 0.5,
            color: 'text.primary',
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)', color: 'primary.main' },
            ...focusRing,
        }}
    >
        <AddIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" sx={{ fontWeight: 500 }}>{label}</Typography>
    </ButtonBase>
);
AvailableChip.propTypes = { label: PropTypes.node, onAdd: PropTypes.func };

// Filled checkbox tile — looks like a pill with a small square check on the
// left + label. Filled blue when checked (matches the schema's tinted look),
// white outlined when not.
export const CheckboxTile = ({ label, checked, onChange }) => (
    <ButtonBase
        onClick={() => onChange && onChange(!checked)}
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            width: '100%',
            px: 1.25,
            py: 0.85,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: checked ? 'primary.light' : 'divider',
            backgroundColor: checked ? 'rgba(25,118,210,0.08)' : '#fff',
            color: checked ? 'primary.main' : 'text.primary',
            textAlign: 'left',
            fontWeight: checked ? 600 : 400,
            transition: 'all 0.12s',
            '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: checked ? 'rgba(25,118,210,0.12)' : 'rgba(25,118,210,0.04)',
            },
            ...focusRing,
        }}
    >
        <Box sx={{
            width: 18, height: 18,
            borderRadius: 0.6,
            border: '1.5px solid',
            borderColor: checked ? 'primary.main' : 'rgba(0,0,0,0.4)',
            backgroundColor: checked ? 'primary.main' : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
        }}>
            {checked && <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>{label}</Typography>
    </ButtonBase>
);
CheckboxTile.propTypes = { label: PropTypes.node, checked: PropTypes.bool, onChange: PropTypes.func };

// Section subtitle inside a card — the small ALL-CAPS labels above each
// chip group ("SECTIONS USED (IN ORDER)", "AVAILABLE (CLICK TO ADD)"). A thin
// bottom rule makes it read clearly as a section heading. Pass `noLine` for the
// tiny field micro-labels (e.g. a colour/size label above a single control)
// where a full-width underline under a narrow column would look off.
export const SubLabel = ({ children, noLine = false, sx = {} }) => (
    <Typography variant="caption" sx={{
        display: 'block',
        fontWeight: 700,
        color: 'text.secondary',
        letterSpacing: 0.5,
        fontSize: '0.7rem',
        mb: 0.75,
        textTransform: 'uppercase',
        ...(noLine ? {} : { pb: 0.5, borderBottom: '1px solid', borderColor: 'divider' }),
        ...sx,
    }}>{children}</Typography>
);
SubLabel.propTypes = { children: PropTypes.node, noLine: PropTypes.bool, sx: PropTypes.object };

// Renders an ordered chip list with an "AVAILABLE" row underneath. Common
// pattern in the PDI modal. `items` is an array of { key, label, used }.
// `setUsed(key, value)` toggles a field's used state.
export const OrderedChipList = ({
    orderedLabel = 'In use',
    availableLabel = 'Available (click to add)',
    items = [],
    setUsed,
}) => {
    const used      = items.filter(it => it.used !== false);
    const available = items.filter(it => it.used === false);
    return (
        <Box>
            <SubLabel>{orderedLabel}</SubLabel>
            <ChipsBox>
                {used.map(it => (
                    <ChipToggle
                        key={it.key}
                        label={it.label}
                        onRemove={() => setUsed && setUsed(it.key, false)}
                    />
                ))}
                {used.length === 0 && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', px: 1, py: 0.5 }}>
                        Nothing here yet — pick from the available row below.
                    </Typography>
                )}
            </ChipsBox>

            {available.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                    <SubLabel>{availableLabel}</SubLabel>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {available.map(it => (
                            <AvailableChip
                                key={it.key}
                                label={it.label}
                                onAdd={() => setUsed && setUsed(it.key, true)}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};
OrderedChipList.propTypes = {
    orderedLabel:   PropTypes.node,
    availableLabel: PropTypes.node,
    items:          PropTypes.array,
    setUsed:        PropTypes.func,
};

// Generic horizontal row used in Related products & variants and Stock
// availability — label+help on the left, control on the right. Defaults
// to center alignment so the control vertically aligns with the label
// block (no white gap below the label when the input is taller).
// Pass `align="top"` for rows whose control is much taller than the
// label, e.g. visual tile pickers.
export const PdiRow = ({ label, help, control, indent = false, align = 'center' }) => (
    <Box sx={{
        display: 'flex',
        alignItems: align === 'top' ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 0.75,
        pl: indent ? 2.5 : 0,
        borderLeft: indent ? '2px solid' : 'none',
        borderColor: indent ? 'divider' : 'transparent',
    }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>{help}</Typography>}
        </Box>
        <Box sx={{ flexShrink: 0, pt: align === 'top' ? 0.25 : 0 }}>{control}</Box>
    </Box>
);
PdiRow.propTypes = { label: PropTypes.node, help: PropTypes.node, control: PropTypes.node, indent: PropTypes.bool, align: PropTypes.oneOf(['top', 'center']) };

// Card + header wrapper used by the page/feature tabs. Outlined Paper with a
// titled header (thin rule underneath) and a spacing-only body. `title` may be
// a plain string (rendered as the standard subtitle) or custom JSX. Inter-row
// rules are off by default — the header rule is the only line; pass
// `divider` to re-enable lines between rows.
export const Section = ({ title, description, children, divider = false, anchor }) => {
    // `anchor` makes the section a search-jump target (id + flash ring), exactly
    // like ConfigCard — so the config search can scroll to it, not just its tab.
    const flash = useAnchor(anchor);
    return (
        <Paper id={anchor || undefined} elevation={0} sx={{
            borderRadius: 2,
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            // Match ConfigCard's chrome: a subtle resting shadow + a gentle lift on
            // hover so page/tab cards read the same as the Opmaak cards.
            boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
            transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
            '&:hover': { boxShadow: '0 6px 20px rgba(16,24,40,0.10)', borderColor: '#d0d5dd' },
            ...(flash && { boxShadow: `0 0 0 3px ${Colors.sft_blue}66`, borderColor: Colors.sft_blue }),
        }}>
            <Box sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                {typeof title === 'string'
                    ? <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
                    : title}
                {description && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{description}</Typography>}
            </Box>
            <Stack spacing={1.5} divider={divider ? <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} /> : null}>
                {children}
            </Stack>
        </Paper>
    );
};
Section.propTypes = { title: PropTypes.node, description: PropTypes.node, children: PropTypes.node, divider: PropTypes.bool, anchor: PropTypes.string };

// Shared empty-state placeholder: a centred icon medallion + message + optional
// action. Use in lists/tabs that can be empty (banners, dynamic pages, vouchers)
// so "nothing here yet" reads consistently and invites the next step.
export const EmptyState = ({ icon, title, description, action }) => (
    <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', gap: 1, py: 6, px: 3, color: 'text.secondary',
    }}>
        <Box sx={{
            width: 48, height: 48, borderRadius: '50%', mb: 0.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(25,118,210,0.08)', color: 'primary.main',
        }}>
            {icon || <InboxOutlinedIcon />}
        </Box>
        {title && <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>{title}</Typography>}
        {description && <Typography variant="caption" sx={{ maxWidth: 320, lineHeight: 1.5 }}>{description}</Typography>}
        {action && <Box sx={{ mt: 1.5 }}>{action}</Box>}
    </Box>
);
EmptyState.propTypes = { icon: PropTypes.node, title: PropTypes.node, description: PropTypes.node, action: PropTypes.node };
